<template>
	<ion-modal :is-open="isOpen" @ion-modal-will-dismiss="closeModal">
		<ion-header>
			<ion-toolbar color="branding">
				<ion-title class="text-lg font-medium">
					{{ $t("user-management.edit-permissions-header") }}
				</ion-title>
				<ion-buttons slot="end">
					<ion-button @click="closeModal">
						<ion-icon :icon="closeOutline" />
					</ion-button>
				</ion-buttons>
			</ion-toolbar>
		</ion-header>

		<ion-content class="ion-padding">
			<RequirePermission :permissions="[PermissionKey.AssignPermissions]">
				<template v-if="!isSubmitting">
					<PermissionTypeList
						v-model="workingPermissions"
						class="rounded-lg bg-gray-100 p-2 pl-3"
						:disabled-permissions="permissionsFromRoles"
						:show-internal="user.internalUser"
					/>
					<div v-if="isError" class="py-2 font-bold text-red-500">
						{{ $t("user-management.permissions-update-error") }}
					</div>
				</template>
				<template v-else>
					<GenericLoader
						:message="$t('user-management.submitting-changes')"
					/>
				</template>
			</RequirePermission>
		</ion-content>

		<ion-footer v-if="!isSubmitting">
			<ion-toolbar>
				<div class="flex flex-row gap-1">
					<ion-button
						color="medium"
						class="flex-1"
						@click="closeModal"
					>
						{{ $t("common.cancel") }}
					</ion-button>
					<ion-button
						v-if="changesMade"
						class="flex-1"
						@click="submit"
					>
						{{ $t("common.save-changes") }}
					</ion-button>
				</div>
			</ion-toolbar>
		</ion-footer>
	</ion-modal>
</template>

<script setup lang="ts">
	import {
		IonButton,
		IonModal,
		IonHeader,
		IonToolbar,
		IonTitle,
		IonButtons,
		IonIcon,
		IonContent,
		IonFooter,
	} from "@ionic/vue";
	import { closeOutline } from "ionicons/icons";
	import { GVAUserPrivilegedDTO } from "seedgreen-shared/dtos/GVAUserDTO";
	import { computed, ref, watch } from "vue";
	import { PermissionKey } from "seedgreen-shared/dtos/PermissionTypeDTO";
	import RequirePermission from "@/components/feature/auth/RequirePermission.vue";
	import {
		useSetUserPermissions,
		useUserPermissionsFromRoles,
	} from "seedgreen-shared/queries/user";
	import GenericLoader from "@/components/ui/GenericLoader.vue";
	import { sendToast } from "@/utils/sendToast";
	import { captureException } from "@sentry/vue";

	import PermissionTypeList from "@/components/feature/user/permission/EditPermissionTypeList.vue";
	import { useI18n } from "vue-i18n";
	import _ from "lodash";

	const { t } = useI18n();
	const workingPermissions = ref<PermissionKey[]>([]);
	const isOpen = ref(false);

	const { setUserPermissions, isError } = useSetUserPermissions();

	const props = defineProps<{
		user: GVAUserPrivilegedDTO;
	}>();

	const userId = computed(() => props.user.id);

	const { permissionsFromRoles } = useUserPermissionsFromRoles(userId);

	watch(
		() => props.user,
		(user) => {
			workingPermissions.value = [...(user.permissions || [])];
		},
		{ immediate: true },
	);

	const changesMade = computed(() => {
		const initialPermissions = props.user.permissions || [];
		const newPermissions = workingPermissions.value || [];
		return !_.isEqual(
			_.sortBy(initialPermissions),
			_.sortBy(newPermissions),
		);
	});

	const isSubmitting = ref(false);
	async function submit() {
		isSubmitting.value = true;
		try {
			await setUserPermissions({
				userId: props.user.id,
				permissions: workingPermissions.value,
			});
			closeModal();
			sendToast(t("user-management.submitting-changes"), 1000, "success");
		} catch (error) {
			console.error(error);
			captureException(error);
		} finally {
			isSubmitting.value = false;
		}
	}

	const openModal = () => {
		workingPermissions.value = [...(props.user.permissions || [])];
		isOpen.value = true;
	};

	const closeModal = async () => {
		isOpen.value = false;
	};

	defineExpose({ openModal, closeModal });
</script>
